import _ from "lodash";

import * as IntegrationsCURD from "app/crud/integrations.crud";
import { setErrorFlag, setLoadingFlag } from "app/store/modules/actions/loading-flag.actions";
import { enqueueSnackbar } from "app/store/modules/actions/notistack.actions";

export const actionTypes = {
  INTEGRATIONS_LOADED: "[INTEGRATIONS] INTEGRATIONS_LOADED",
  INTEGRATION_ADDED: "[INTEGRATIONS] INTEGRATION_ADDED",
  INTEGRATION_UPDATED: "[INTEGRATIONS] INTEGRATION_UPDATED",
  INTEGRATION_REGENERATED: "[INTEGRATIONS] INTEGRATION_REGENERATED",
};

export const flagNames = {
  LOADING_INTEGRATIONS: "[INTEGRATIONS] LOADING_INTEGRATIONS",
  ADDING_INTEGRATION: "[INTEGRATIONS] ADDING_INTEGRATION",
  UPDATING_INTEGRATION: "[INTEGRATIONS] UPDATING_INTEGRATION",
  REGENERATING_INTEGRATION: "[INTEGRATIONS] REGENERATING_INTEGRATION",
  SYNC_GOOGLE_DRIVE: "[INTEGRATIONS] SYNC_GOOGLE_DRIVE",
  SYNC_ONEDRIVE: "[INTEGRATIONS] SYNC_ONEDRIVE",
  VALIDATE_GOOGLE_DRIVE: "[INTEGRATIONS] VALIDATE_GOOGLE_DRIVE",
  VALIDATE_ONEDRIVE: "[INTEGRATIONS] VALIDATE_ONEDRIVE",
  DISCONNECT_GOOGLE_DRIVE: "[INTEGRATIONS] DISCONNECT_GOOGLE_DRIVE",
  DISCONNECT_ONEDRIVE: "[INTEGRATIONS] DISCONNECT_ONEDRIVE",
};

export const loadIntegrations = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.LOADING_INTEGRATIONS, true));

    try {
      const { status, data } = await IntegrationsCURD.getIntegrations();

      if (status === 200) {
        dispatch({
          type: actionTypes.INTEGRATIONS_LOADED,
          payload: data,
        });
      } else {
        dispatch(setErrorFlag(flagNames.LOADING_INTEGRATIONS, data.message));
      }

      dispatch(setLoadingFlag(flagNames.LOADING_INTEGRATIONS, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.LOADING_INTEGRATIONS, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const addConfig = (integrationId, isActive, configObj) => {
  return async (dispatch, getStore) => {
    dispatch(setLoadingFlag(flagNames.ADDING_INTEGRATION, integrationId));

    try {
      const store = getStore();
      const userId = _.get(store, "auth.user._id", null);
      const { status, data } = await IntegrationsCURD.addIntegrationConfig(integrationId, {
        userId,
        isActive,
        configObj,
      });

      if (status === 200) {
        dispatch({
          type: actionTypes.INTEGRATION_ADDED,
          payload: data.data.integrationConfig,
        });
      } else {
        dispatch(setErrorFlag(flagNames.ADDING_INTEGRATION, data.message));
      }

      dispatch(setLoadingFlag(flagNames.ADDING_INTEGRATION, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.ADDING_INTEGRATION, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const updateConfig = (integrationConfigId, isActive, configObj) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.UPDATING_INTEGRATION, integrationConfigId));

    try {
      const { status, data } = await IntegrationsCURD.updateIntegrationConfig(integrationConfigId, {
        isActive,
        configObj,
      });

      if (status === 200) {
        dispatch({
          type: actionTypes.INTEGRATION_UPDATED,
          payload: data,
        });
      } else {
        dispatch(setErrorFlag(flagNames.UPDATING_INTEGRATION, data.message));
      }

      dispatch(setLoadingFlag(flagNames.UPDATING_INTEGRATION, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.UPDATING_INTEGRATION, e.toString()));
      return Promise.reject(e);
    }
  };
};

// specifically used for zapier config when user enables the integration for the first time
export const activateConfig = (integrationConfigId) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.UPDATING_INTEGRATION, integrationConfigId));

    try {
      const { status, data } = await IntegrationsCURD.activateIntegration(integrationConfigId);

      if (status === 200) {
        dispatch(loadIntegrations());
      } else {
        dispatch(setErrorFlag(flagNames.UPDATING_INTEGRATION, data.message));
      }

      dispatch(setLoadingFlag(flagNames.UPDATING_INTEGRATION, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.UPDATING_INTEGRATION, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const regenerateConfig = (integrationConfigId) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.REGENERATING_INTEGRATION, integrationConfigId));

    try {
      const { status, data } =
        await IntegrationsCURD.regenerateIntegrationConfig(integrationConfigId);

      if (status === 200) {
        await dispatch(loadIntegrations());
      } else {
        dispatch(setErrorFlag(flagNames.REGENERATING_INTEGRATION, data.message));
      }

      dispatch(setLoadingFlag(flagNames.REGENERATING_INTEGRATION, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      dispatch(setErrorFlag(flagNames.REGENERATING_INTEGRATION, e.toString()));
      return Promise.reject(e);
    }
  };
};

export const validateGoogleDriveConnection = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.VALIDATE_GOOGLE_DRIVE, true));
    try {
      const { status, data } = await IntegrationsCURD.validateGoogleDriveConnection();
      if (status === 200) {
        return data.data;
      }
      throw new Error();
    } finally {
      dispatch(setLoadingFlag(flagNames.VALIDATE_GOOGLE_DRIVE, false));
    }
  };
};

export const validateOneDriveConnection = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.VALIDATE_ONEDRIVE, true));
    try {
      const { status, data } = await IntegrationsCURD.validateOneDriveConnection();
      if (status === 200) {
        return data.data;
      }
      throw new Error();
    } finally {
      dispatch(setLoadingFlag(flagNames.VALIDATE_ONEDRIVE, false));
    }
  };
};

export const disconnectGoogleDrive = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.DISCONNECT_GOOGLE_DRIVE, true));
    try {
      await IntegrationsCURD.disconnectGoogleDrive();
      await dispatch(loadIntegrations());
    } finally {
      dispatch(setLoadingFlag(flagNames.DISCONNECT_GOOGLE_DRIVE, false));
    }
  };
};

export const disconnectOneDrive = () => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.DISCONNECT_ONEDRIVE, true));
    try {
      await IntegrationsCURD.disconnectOneDrive();
      await dispatch(loadIntegrations());
    } finally {
      dispatch(setLoadingFlag(flagNames.DISCONNECT_ONEDRIVE, false));
    }
  };
};

export const syncGoogleDriveProvider = (providerId, requestId) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.SYNC_GOOGLE_DRIVE, providerId));

    try {
      const { status, data } = await IntegrationsCURD.syncGoogleDriveForProvider(
        providerId,
        requestId
      );

      dispatch(
        enqueueSnackbar({
          message: data.message,
          options: {
            autoHideDuration: 6000,
            key: new Date().getTime() + Math.random(),
            variant: status === 200 ? "success" : "error",
          },
        })
      );

      if (status !== 200) {
        dispatch(setErrorFlag(flagNames.SYNC_GOOGLE_DRIVE, data.message));
      }

      dispatch(setLoadingFlag(flagNames.SYNC_GOOGLE_DRIVE, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = _.get(e, "response.data.error.message", e.toString());
      dispatch(setErrorFlag(flagNames.SYNC_GOOGLE_DRIVE, errorMessage));
      dispatch(
        enqueueSnackbar({
          message: errorMessage,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: "error",
          },
        })
      );
      return Promise.reject(e);
    }
  };
};

export const syncOneDriveProvider = (providerId, requestId) => {
  return async (dispatch) => {
    dispatch(setLoadingFlag(flagNames.SYNC_ONEDRIVE, providerId));

    try {
      const { status, data } = await IntegrationsCURD.syncStorageProvider(
        providerId,
        requestId,
        "ONEDRIVE"
      );

      dispatch(
        enqueueSnackbar({
          message: data.message,
          options: {
            autoHideDuration: 6000,
            key: new Date().getTime() + Math.random(),
            variant: status === 200 ? "success" : "error",
          },
        })
      );

      if (status !== 200) {
        dispatch(setErrorFlag(flagNames.SYNC_ONEDRIVE, data.message));
      }

      dispatch(setLoadingFlag(flagNames.SYNC_ONEDRIVE, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = _.get(e, "response.data.error.message", e.toString());
      dispatch(setErrorFlag(flagNames.SYNC_ONEDRIVE, errorMessage));
      dispatch(
        enqueueSnackbar({
          message: errorMessage,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: "error",
          },
        })
      );
      return Promise.reject(e);
    }
  };
};

export const syncCloudStorageProvider = (providerId, requestId, provider) => {
  return async (dispatch) => {
    const flagName =
      provider === "ONEDRIVE" ? flagNames.SYNC_ONEDRIVE : flagNames.SYNC_GOOGLE_DRIVE;
    dispatch(setLoadingFlag(flagName, providerId));

    try {
      const { status, data } = await IntegrationsCURD.syncStorageProvider(
        providerId,
        requestId,
        provider
      );

      dispatch(
        enqueueSnackbar({
          message: data.message,
          options: {
            autoHideDuration: 6000,
            key: new Date().getTime() + Math.random(),
            variant: status === 200 ? "success" : "error",
          },
        })
      );

      if (status !== 200) {
        dispatch(setErrorFlag(flagName, data.message));
      }

      dispatch(setLoadingFlag(flagName, false));
      return Promise.resolve({ status, data });
    } catch (e) {
      const errorMessage = _.get(e, "response.data.error.message", e.toString());
      dispatch(setErrorFlag(flagName, errorMessage));
      dispatch(
        enqueueSnackbar({
          message: errorMessage,
          options: {
            key: new Date().getTime() + Math.random(),
            variant: "error",
          },
        })
      );
      return Promise.reject(e);
    }
  };
};
